import React, { useState } from "react";
import styled from "styled-components";
import { func, object as obj, bool } from "prop-types";

import PaginatedWrapper from "components/shared/PaginatedWrapper";
import {
  basicDetailsSubmit,
  emailDetailsSubmit,
  phoneDetailsSubmit
} from "data/segmentCustomEvents";

import AdUnitPage from "components/onboarding/form_pages/AdUnitPage";
import CarePage from "components/onboarding/form_pages/CarePage";
import EmailAndSubmissionPage from "components/onboarding/form_pages/EmailAndSubmissionPage";
import PhonePage from "components/onboarding/form_pages/PhonePage";
import SuccessPage from "components/onboarding/form_pages/SuccessPage";

import {
  validateFields,
  validateField
} from "components/onboarding/OnboardingHelper";
import { splitFullName } from "utils";

import ExitButton from "components/shared/ExitButton";

const OnboardingFormPopup = styled.div`
  background: white;
  width: 100vw;
  transform: translateY(100vh);
  animation: ${({ formClosing }) =>
    formClosing ? "float-out 1s forwards" : "float-in 1s forwards"};
  ${({ isMobile }) =>
    isMobile &&
    `
      position: fixed;
      overflow-y: scroll;
      height: ${window.innerHeight}px;
      top: 0;
      bottom: 0;
    `}
  

  @media (min-width: ${({ theme }) => theme.screen.tablet}px) {
    animation: float-in 1s forwards;
    border-radius: 10px;
    max-width: 550px;
    min-height: 400px;
  }

  @keyframes float-in {
    from {
      transform: translateY(100vh);
    }
    to {
      transform: translateY(0);
    }
  }

  @keyframes float-out {
    from {
      transform: translateY(0);
    }
    to {
      transform: translateY(100vh);
    }
  }
`;

const OnboardingFormWrapper = styled.form`
  height: 100%;
`;

const Page = styled.div`
  text-align: center;
  padding: ${({ padding }) => padding || "2rem 0.5rem"};
  animation: appear 1s forwards;

  @keyframes appear {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const OnboardingFormPaginated = ({
  formData,
  isMobile,
  loading,
  setFormData,
  submit,
  taxonomies,
  toggleFormOpenState
}) => {
  const [invalidMessages, setInvalidMessages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [formClosing, setFormClosing] = useState(false);

  const closeForm = () => {
    setFormClosing(true);
    setTimeout(() => {
      setFormClosing(false);
      toggleFormOpenState(false);
    }, 1000);
  };

  const fieldValidation = async e => {
    let { name, value } = e.target;
    if (name === "name") {
      name = "lastname";
      value = splitFullName(value).lastname;
    }
    const invalidMessage = await validateField(name, value);
    setInvalidMessages({ ...invalidMessages, [name]: invalidMessage });
  };

  const handleChange = async e => {
    const formField = e.target.name;
    const formValue =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    if (formField === "name") {
      setFormData({
        ...formData,
        name: e.target.value,
        ...splitFullName(e.target.value)
      });
    } else {
      setFormData({ ...formData, [formField]: formValue });
    }
  };

  const preSubmit = async event => {
    event.preventDefault();
    const validationFields = Object.keys(formData).filter(
      field => field !== "newsLetterSignup"
    );
    const checkValidation = await validateFields(formData, validationFields);
    if (Object.values(checkValidation).some(invalid => invalid)) {
      setInvalidMessages(checkValidation);
    } else {
      emailDetailsSubmit();
      const success = await submit(formData);
      if (success) {
        setCurrentPage(currentPage + 1);
      }
    }
  };

  const updatePage = async (page, fields) => {
    if (!fields) {
      setCurrentPage(page);
    } else {
      const validatedMessages = await validateFields(formData, fields);
      setInvalidMessages({ ...invalidMessages, ...validatedMessages });
      if (!Object.values(validatedMessages).some(invalid => invalid)) {
        setCurrentPage(page);
      }
    }
  };

  return (
    <OnboardingFormPopup
      className="onboarding-form"
      formClosing={formClosing}
      isMobile={isMobile}
      data-testid="onboarding-form"
    >
      {toggleFormOpenState && currentPage === 1 && (
        <ExitButton
          onClick={() => (isMobile ? closeForm() : toggleFormOpenState(false))}
        />
      )}
      <OnboardingFormWrapper onSubmit={preSubmit}>
        <PaginatedWrapper
          currentPage={currentPage}
          setCurrentPage={updatePage}
          loading={loading}
        >
          <AdUnitPage noButtons setCurrentPage={setCurrentPage} />
          <Page
            fields={["lastname", "requested_care_type", "relationship"]}
            pageUpdateAction={basicDetailsSubmit}
          >
            <CarePage
              formData={formData}
              fieldValidation={fieldValidation}
              handleChange={handleChange}
              invalidMessages={invalidMessages}
              taxonomies={taxonomies}
            />
          </Page>
          <Page fields={["phone_number"]} pageUpdateAction={phoneDetailsSubmit}>
            <PhonePage
              formData={formData}
              handleChange={handleChange}
              invalidMessages={invalidMessages}
              fieldValidation={fieldValidation}
            />
          </Page>
          <Page
            fields={["email", "confirmed_tos"]}
            submitClass="onboarding__submit-button"
            submissionPage
            submitAction={preSubmit}
          >
            <EmailAndSubmissionPage
              formData={formData}
              handleChange={handleChange}
              invalidMessages={invalidMessages}
              fieldValidation={fieldValidation}
            />
          </Page>
          <Page noButtons padding="0">
            <SuccessPage />
          </Page>
        </PaginatedWrapper>
      </OnboardingFormWrapper>
    </OnboardingFormPopup>
  );
};

OnboardingFormPaginated.propTypes = {
  formData: obj.isRequired,
  isMobile: bool,
  loading: bool.isRequired,
  setFormData: func.isRequired,
  submit: func.isRequired,
  taxonomies: obj.isRequired,
  toggleFormOpenState: func
};

export default OnboardingFormPaginated;
